/**
 * The last answer each dashboard query received, so re-entering a page renders
 * it at once and revalidates behind it instead of showing a placeholder for as
 * long as the query takes. Memory only: it lives as long as the tab, is dropped
 * on logout, and never reaches storage.
 */

interface CacheEntry {
  data: unknown;
  storedAt: number;
}

/** Enough for every dashboard a session visits, without holding old filter combinations forever. */
const MAX_ENTRIES = 24;
/**
 * Past this the cached answer is dropped rather than shown: the request that
 * would replace it is already in flight, and a page closed for half an hour is
 * better off with its loading state than with numbers nobody would recognize.
 */
const MAX_AGE_MS = 30 * 60 * 1000;

const SEPARATOR = '|';

const entries = new Map<string, CacheEntry>();

/** One entry per dashboard and query: the page's own key, its filters and any extra query identity. */
export function dashboardCacheKey(
  cacheKey: string,
  fetchKey: string,
  refetchKey: string | number | null | undefined
): string {
  return [cacheKey, fetchKey, String(refetchKey ?? '')].join(SEPARATOR);
}

export function readDashboardCache<T>(key: string, now = Date.now()): T | null {
  const entry = entries.get(key);
  if (!entry) return null;
  if (now - entry.storedAt > MAX_AGE_MS) {
    entries.delete(key);
    return null;
  }
  // Re-insert so the least recently read entry is the one evicted next.
  entries.delete(key);
  entries.set(key, entry);
  return entry.data as T;
}

export function writeDashboardCache(key: string, data: unknown, now = Date.now()): void {
  entries.delete(key);
  entries.set(key, { data, storedAt: now });
  while (entries.size > MAX_ENTRIES) {
    const oldest = entries.keys().next();
    if (oldest.done) break;
    entries.delete(oldest.value);
  }
}

/** Drop everything; the next user of this tab must not see the last one's data. */
export function clearDashboardCache(): void {
  entries.clear();
}
